"use client";

import * as React from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";

/**
 * 문제 포럼 상세 좌측 — 같은 scope 관련 문제 네비 (JCG-2105).
 *
 * scope 가 바뀔 때마다 `/api/forum/sibling-problems?scope=...` 를 fetch 해 정렬된 관련
 * 문제 ID 목록을 세로 리스트로 그린다. 현재 problemId 는 앰버로 강조(IconRail active 패턴
 * — `bg-card text-amber-600 dark:text-amber-400` + 좌측 앰버 인디케이터).
 *
 * 셸은 포럼 상세 간 소프트 네비게이션에서 언마운트되지 않으므로, scope/problemId 변화에
 * 반응해 fetch·강조가 갱신돼야 한다(scope 를 effect deps 에 포함).
 *
 * 색상은 전역 디자인 토큰 + 브랜드 앰버만 사용 → 다크모드 자동 대응.
 */
interface SiblingNavProps {
  subject: string;
  scope: string;
  /** 현재 보고 있는 문제 ID — 리스트에서 강조 */
  currentId: string;
}

/**
 * scope 와 함께 저장해 scope 변화 시 렌더 타임에 loading 상태를 파생할 수 있도록 한다.
 * effect 내에서 동기 setState 를 사용하면 ESLint react-hooks/set-state-in-effect 에 위반되므로,
 * 이전 scope 를 상태에 포함시켜 비교하는 방식으로 loading 리셋을 처리한다.
 */
interface SiblingState {
  scope: string;
  status: "loading" | "ready" | "error";
  ids: string[];
}

const activeIndicatorCls =
  "absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-amber-500";

export default function SiblingNav({ subject, scope, currentId }: SiblingNavProps) {
  const [state, setState] = React.useState<SiblingState>({
    scope,
    status: "loading",
    ids: [],
  });

  // scope 바뀔 때마다 관련 문제 목록 재조회. 응답 도착 전 scope 가 또 바뀌면 stale 응답을 무시
  // (cancelled 가드)해 이전 scope 결과가 현재 목록을 덮어쓰지 않게 한다.
  // loading 리셋은 setState를 동기 호출하는 대신 scope를 상태에 포함시켜 렌더 타임에 파생한다.
  React.useEffect(() => {
    let cancelled = false;

    fetch(`/api/forum/sibling-problems?scope=${encodeURIComponent(scope)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data: { success?: boolean; ids?: string[] }) => {
        if (cancelled) return;
        if (data.success && Array.isArray(data.ids)) {
          setState({ scope, status: "ready", ids: data.ids });
        } else {
          setState({ scope, status: "error", ids: [] });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ scope, status: "error", ids: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [scope]);

  // scope 가 바뀐 직후(fetch 완료 전)에는 loading 스켈레톤을 보여준다.
  const effectiveStatus = state.scope !== scope ? "loading" : state.status;
  const effectiveIds = state.scope !== scope ? [] : state.ids;

  // 상태(로딩→완료/오류) 전환을 스크린리더에 알리기 위해 영역을 aria-live="polite" 로 감싼다.
  let body: React.ReactNode;
  if (effectiveStatus === "loading") {
    body = (
      <ul className="flex flex-col gap-1.5" aria-busy="true">
        {Array.from({ length: 8 }).map((_, i) => (
          <li
            key={i}
            className="h-8 animate-pulse rounded-lg bg-muted"
            aria-hidden="true"
          />
        ))}
      </ul>
    );
  } else if (effectiveStatus === "error") {
    body = (
      <p className="px-1 py-2 text-xs text-muted-foreground">
        관련 문제 목록을 불러오지 못했어요.
      </p>
    );
  } else if (effectiveIds.length === 0) {
    body = (
      <p className="px-1 py-2 text-xs text-muted-foreground">
        같은 범위의 다른 문제가 없어요.
      </p>
    );
  } else {
    body = (
      <ul className="flex flex-col gap-0.5">
        {effectiveIds.map((id) => {
          const on = id === currentId;
          return (
            <li key={id}>
              <Link
                href={`/forum/jcg/${subject}/${scope}/${id}`}
                aria-current={on ? "page" : undefined}
                className={cn(
                  "relative flex items-center rounded-lg px-2.5 py-1.5 font-mono text-xs transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
                  on
                    ? "bg-card font-bold text-amber-600 shadow-sm dark:text-amber-400"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {on && <span className={activeIndicatorCls} aria-hidden="true" />}
                <span className="truncate">{id}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <nav aria-label="같은 범위 관련 문제" aria-live="polite">
      {body}
    </nav>
  );
}
